import i18next from "i18next";
import { isValidElement, type ReactNode } from "react";

import type { ToastrConfig, ToastrErrorInput, ToastrMessage } from "./Toastr";

const activeToasts = new Set<string>();

function toastKey(
  type: string,
  message: ReactNode,
  buttonLabel?: string
): string | null {
  if (typeof message !== "string") return null;
  return `${type}::${message}::${buttonLabel ?? ""}`;
}

function getContentDir(): "ltr" | "rtl" {
  if (typeof document === "undefined") return "ltr";
  return (
    (document.documentElement.getAttribute("dir") as "ltr" | "rtl") ??
    (document.body.getAttribute("dir") as "ltr" | "rtl") ??
    "ltr"
  );
}

function resolveMessage(message: ToastrMessage): ReactNode {
  if (typeof message === "string") return message;
  if (isValidElement(message)) return message;
  // Arrays of ReactNodes (e.g. [<a/>, " ", <b/>]) render natively via {message}.
  if (Array.isArray(message)) return message;
  if (message && typeof message === "object") {
    const obj = message as {
      noticeCode?: string;
      customMessage?: string;
      notice?: string;
    };
    if (obj.customMessage) return obj.customMessage;
    if (obj.notice) return obj.notice;
    if (obj.noticeCode) return i18next.t(obj.noticeCode);
  }
  return String(message);
}

function resolveErrorMessage(errorObject: ToastrErrorInput): ReactNode {
  if (typeof errorObject === "string") return errorObject;

  if (errorObject && typeof errorObject === "object") {
    // Axios-style error
    const axiosLike = errorObject as {
      response?: { data?: Record<string, unknown> };
      message?: string;
      isAxiosError?: boolean;
    };

    if (axiosLike.response?.data) {
      const data = axiosLike.response.data;
      if (data.customMessage) return String(data.customMessage);
      if (data.error) return String(data.error);
      if (Array.isArray(data.errors)) return data.errors.join("\n");
      if (data.noticeCode) return String(data.noticeCode);
    }

    if (axiosLike.isAxiosError && axiosLike.message) return axiosLike.message;

    // Native Error
    if (errorObject instanceof Error) return errorObject.message;

    // ToastrMessage (string, React element, array, or notice-shape object)
    return resolveMessage(errorObject as ToastrMessage);
  }

  return "Something went wrong.";
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);

    return true;
  } catch {
    // Clipboard API is unavailable in insecure contexts; fall back to execCommand.
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const isCopied = document.execCommand("copy");
      textArea.remove();

      return isCopied;
    } catch {
      return false;
    }
  }
}

function parseConfig(args: unknown[]): ToastrConfig {
  if (args.length === 0) return {};

  const first = args[0];
  if (typeof first === "object" && first !== null && !Array.isArray(first)) {
    return first as ToastrConfig;
  }

  // Legacy positional args: buttonLabel, onClick, customConfig, showIcon
  const [buttonLabel, onClick, customConfig, showIcon] = args as [
    string?,
    (() => void)?,
    Record<string, unknown>?,
    boolean?,
  ];

  return {
    ...(buttonLabel ? { buttonLabel } : {}),
    ...(onClick ? { onClick } : {}),
    ...(showIcon !== undefined ? { showIcon } : {}),
    ...(customConfig ?? {}),
  };
}

export {
  activeToasts,
  copyToClipboard,
  getContentDir,
  parseConfig,
  resolveErrorMessage,
  resolveMessage,
  toastKey,
};
